import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import AppButton from '../../../components/AppButton';
import JobDetails from '../JobDetails';
import Opportunity from '../../../models/Opportunity';
import ProjectPosition from '../../../models/ProjectPosition';
import JobMatchScoreModal from './JobMatchScoreModal';
import JobReferralModal from './JobReferralModal';
import { useAuth } from '../../../providers/AuthProvider';
import OpportunityProvider from '../../../providers/OpportunityProvider';
import { OppProposalModal } from '../../proposals/OppProposalWalkthrough';
import { LISTING_STATUS_OPTIONS } from '../../project-bidding/PositionShortlistedCandidates';

/**
 * Job card for the redesigned job board.
 * Same rules/actions as JobPositionPanel (board mode — no preview / showDetails / opportunity).
 */

const htmlToText = (html = '') => {
    if (!html) return '';
    try {
        return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) {
        return TB.stripHtml(html);
    }
};

const contractTypeClass = job => {
    if (job.isFixedFee()) return 'text-amber-700';
    if (job.isFixedRetainer()) return 'text-rose-600';
    return 'text-primary';
};

// Same fields as JobPositionPanel's jobInfo, per contract type
const getJobDetails = (job, auth) => {
    const details = [];

    details.push({
        label: 'Location',
        value: (
            <>
                {laravel.project_visibility_location_settings[job.project?.visibility_setting_location]}
                {job.project?.visibility_setting_location > 2 && ` of ${job.project?.location?.full}`}
            </>
        ),
    });

    details.push({
        label: 'Contract',
        value: (
            <span className={contractTypeClass(job)}>
                {job.contractTypeName()}
                {!!job.isFixedRetainer() && <span className="capitalize"> – {job.payout_frequency}</span>}
            </span>
        ),
    });

    const hours = parseInt(job.avg_hours_per_period);
    const timeRange = job.timeRange(' – ');
    const estimatedHours = `${job.estimated_total_periods || job.total_hours} hrs`;

    if (!auth.isTalentSupplierResource()) {
        let estimatedPayout = TB.formatCurrency(job.estimated_payout);
        if (!job.targetRate() && job.estimated_min_payout) {
            estimatedPayout = `${TB.formatCurrency(job.estimated_min_payout)} – ${TB.formatCurrency(job.estimated_max_payout)}`;
        }

        if (job.isFixedFee()) {
            details.push({ label: 'Est. payout', value: estimatedPayout });
        }
        if (job.isTimeMaterials()) {
            details.push({ label: job.targetRate() ? 'Hourly rate' : 'Est. hourly rate', value: job.estimatedRate() });
        }
    }

    if (job.isFixedFee()) {
        details.push(
            { label: 'Deliverables', value: job.deliverables?.length || 1 },
            { label: 'Duration', value: timeRange },
        );
    } else if (job.isFixedRetainer()) {
        details.push(
            { label: `Est. payout / ${job.payout_frequency === 'monthly' ? 'month' : 'week'}`, value: TB.formatCurrency(job.payout_amount) },
            { label: 'Retainer duration', value: job.payoutFrequencyLabel() },
            { label: 'Total est. payout', value: TB.formatCurrency(job.estimated_payout) },
        );
    } else {
        details.push(
            { label: 'Duration', value: timeRange },
            { label: 'Avg hrs / week', value: !TB.isNaN(hours) ? TB.pluralize('hr', hours, true) : '—' },
            { label: 'Total est. hours', value: estimatedHours },
        );
    }

    return details;
};

const ListingStatus = ({ job }) => {
    const status = LISTING_STATUS_OPTIONS.find(option => option.value === (job.listing_status || 1)) || LISTING_STATUS_OPTIONS[0];
    const total = LISTING_STATUS_OPTIONS.length;

    return (
        <Tooltip placement="top" color="#000" title={<span className="text-xs italic text-white">{status.description}</span>}>
            <span className="inline-flex items-center gap-2 rounded-md bg-white/95 px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm">
                {status.label} {status.value}/{total}
                <span className="h-1 w-12 overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
                    <span className="block h-full rounded-full bg-primary-lighter" style={{ width: `${(status.value / total) * 100}%` }} />
                </span>
            </span>
        </Tooltip>
    );
};

const MatchBadge = ({ isMatch, oppStatus }) => {
    if (oppStatus) {
        return <span className="rounded-md bg-primary px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">{oppStatus}</span>;
    }
    return isMatch ? (
        <span className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
            <CheckOutlined className="text-[10px]" aria-hidden="true" /> Role match
        </span>
    ) : (
        <span className="rounded-md bg-white/95 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-700">
            No role match
        </span>
    );
};

const JobBoardCard = ({ job, disabled = false, onUpdateJob, onRequest }) => {
    const { auth } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [oppForProposal, setOppForProposal] = useState(null);
    const [bannerFailed, setBannerFailed] = useState(false);

    const canManage = auth.canManageClientResourceTalent();
    const detailPath = JobDetails.pathForJob(job);

    const relatedOppId = job.related_opportunity_id;
    const oppStatus = job.related_opportunity_status
        ? Opportunity.displayStatusFor(job.related_opportunity_status)
        : null;
    const isMatch = !!relatedOppId || job.is_match;
    const proposalPath = relatedOppId && `/talent/project/${relatedOppId}/proposal`;

    const bannerSrc = (!bannerFailed && job.client?.banner_image_path) || TB.defaultClientBannerImage();
    const logoSrc = job.client?.logo_path || TB.defaultClientLogoImage();
    const description = htmlToText(job.description || job.project?.description);
    const details = getJobDetails(job, auth);

    /* ---------------------------------- Actions (same as JobPositionPanel) ---------------------------------- */

    const onToggleInterest = async () => {
        let response;

        if (job.has_shown_interest_in_job) {
            const confirm = await TB.alert('warning', 'Withdraw Interest?',
                'Are you sure you want to withdraw your interest for this job? Access to client and proposal will be lost',
                [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
            );
            if (!confirm) return;

            response = await onRequest(() => auth.toggleInterestForJob(job));
        } else {
            // Only keep the interest response; turning availability on is a side effect
            const results = await onRequest(() => Promise.all([
                auth.proposeInterestForJob(job),
                !auth.is_available ? TB.auth().talentUpdate({ is_available: true }) : Promise.resolve('Skip talent update'),
            ]));
            response = results?.[0];
        }

        if (!response) {
            return TB.showServerErrorToast();
        }

        if (response instanceof ProjectPosition) {
            await onUpdateJob(response);
            await TB.event('Modal@hide');
            await TB.wait(300);
            await TB.toast('Interest Withdrawn for Job');
        } else if (response instanceof Opportunity) {
            await TB.sysAlert('talent.propose_interest_and_begin_proposal', undefined, { icon: 'fas fa-thumbs-up' });

            if (searchParams?.has('proposal')) {
                const params = new URLSearchParams(searchParams);
                params.delete('proposal');
                await setSearchParams(params);
            }
            await setOppForProposal(response);

            const params = new URLSearchParams(searchParams);
            params.set('proposal', '1');
            await setSearchParams(params);
        }
    };

    const onReferJob = () => TB.event('Modal@show', {
        modalClass: 'job-referral-modal-form',
        content: (
            <JobReferralModal
                job={job}
                user={auth}
                onRequestClose={() => TB.event('Modal@hide')}
                disabled={disabled}
                onRequest={onRequest}
                onSuccess={async () => {
                    await TB.event('Modal@hide');
                    await TB.wait(300);
                    await TB.sysAlert('talent.job_referral_submitted_success');
                }}
            />
        ),
    });

    const onShowMatchDetails = () => TB.event('Modal@show', {
        modalClass: 'job-match-details-modal',
        content: (
            <JobMatchScoreModal
                key={TB.uuid() + job.id}
                job={job}
                user={auth}
                disabled={disabled}
                skipMatch={false}
                onRequest={onRequest}
                onScrollTop={() => TB.event('Modal@scroll')}
                onRequestClose={() => TB.event('Modal@hide')}
                onUpdateJob={onUpdateJob}
            />
        ),
    });

    /* ---------------------------------- Render ---------------------------------- */

    const renderActions = () => {
        if (job.has_shown_interest_in_job) {
            return (
                <>
                    {!relatedOppId && job.is_match && (
                        <AppButton variant="success" size="lg" block disabled={disabled} icon={<CheckOutlined />} onClick={onShowMatchDetails}>
                            Interested
                        </AppButton>
                    )}
                    {proposalPath && (
                        <AppButton size="lg" block to={proposalPath}>
                            {auth.isTalentSupplierResource() ? 'View details' : 'Edit proposal'}
                        </AppButton>
                    )}
                    {!canManage && !auth.isTalentSupplierResource() && (
                        <AppButton variant="link" size="md" block disabled={disabled} onClick={onToggleInterest}>
                            Withdraw interest
                        </AppButton>
                    )}
                </>
            );
        }

        if (canManage) return null;

        return (
            <>
                {job.can_show_interest_in_job && (
                    <AppButton size="lg" block disabled={disabled} onClick={onToggleInterest}>
                        I'm interested
                    </AppButton>
                )}
                {job.is_match ? (
                    <AppButton variant="success" size="lg" block disabled={disabled} onClick={onShowMatchDetails}>
                        See match score
                    </AppButton>
                ) : (
                    <>
                        <p className="m-0 text-center text-sm text-gray-500">Have a friend who may be interested?</p>
                        <AppButton variant="secondary" size="lg" block disabled={disabled} onClick={onReferJob}>
                            Refer someone
                        </AppButton>
                    </>
                )}
            </>
        );
    };

    return (
        <article className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)]">
            {/* Banner */}
            <Link to={proposalPath || detailPath} className="relative block h-36 shrink-0 bg-gray-200 sm:h-40" aria-label={`View ${job.title}`}>
                <img
                    src={bannerSrc}
                    alt=""
                    loading="lazy"
                    onError={() => setBannerFailed(true)}
                    className="h-full w-full object-cover"
                />
                <span className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20" aria-hidden="true" />

                <span className="absolute left-4 top-4"><MatchBadge isMatch={isMatch} oppStatus={oppStatus} /></span>
                <span className="absolute bottom-3 left-4" onClick={e => e.preventDefault()}><ListingStatus job={job} /></span>
            </Link>

            {/* Body */}
            <div className="relative flex flex-1 flex-col px-4 pb-5 pt-4 sm:px-5">
                <span className="absolute -top-12 right-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white p-1.5 shadow-md sm:right-5 sm:h-[88px] sm:w-[88px]">
                    <img src={logoSrc} alt={job.client?.name ? `${job.client.name} logo` : ''} className="max-h-full max-w-full rounded-xl object-contain" />
                </span>

                <p className="m-0 flex items-center gap-2 pr-24 text-sm text-gray-500">
                    Posted {TB.moment(job.posted_at).fromNow()}
                    {job.is_new && (
                        <span className="rounded-md bg-danger px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">New</span>
                    )}
                </p>

                <Link to={detailPath} className="mt-1.5 block text-lg font-semibold leading-snug text-gray-900 hover:text-primary hover:no-underline sm:text-xl">
                    {job.title}
                </Link>
                <Link to={detailPath} className="mt-0.5 block text-sm text-gray-600 hover:text-primary hover:no-underline">
                    {[job.client?.name, job.project?.name].filter(Boolean).join(' · ')}
                </Link>

                {description && (
                    <p className="m-0 mt-4 line-clamp-2 text-sm leading-relaxed text-gray-600">{description}</p>
                )}

                {/* Spacer pushes the details + actions to the bottom so cards in a row line up */}
                <div className="min-h-[16px] flex-1" aria-hidden="true" />

                <dl className="m-0 grid grid-cols-2 overflow-hidden rounded-xl border border-solid border-gray-200 [&>div:nth-child(even)]:border-l [&>div:nth-child(n+3)]:border-t [&>div]:border-0 [&>div]:border-solid [&>div]:border-gray-200">
                    {details.map(({ label, value }) => (
                        <div key={label} className="min-w-0 px-3 py-2.5">
                            <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">{label}</dt>
                            <dd className="m-0 mt-0.5 break-words text-sm font-semibold text-gray-900">{value}</dd>
                        </div>
                    ))}
                </dl>

                <div className="mt-5 flex flex-col gap-2.5">
                    {renderActions()}
                </div>
            </div>

            {oppForProposal && (
                <OpportunityProvider
                    model={oppForProposal}
                    onReset={async (opp) => {
                        await onUpdateJob(opp.relatedJob());
                        return setOppForProposal(opp);
                    }}
                >
                    <OppProposalModal
                        onClose={async (updatedJob) => {
                            if (updatedJob) await onUpdateJob(updatedJob);
                            return setOppForProposal(null);
                        }}
                    />
                </OpportunityProvider>
            )}
        </article>
    );
};

/**
 * Loading placeholder that mirrors JobBoardCard's layout (banner, logo, header, details grid, actions).
 */
const Bone = ({ className = '' }) => <span className={`block rounded-md bg-gray-200 ${className}`} />;

export const JobBoardCardSkeleton = () => (
    <div
        className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)] motion-safe:animate-pulse"
        aria-hidden="true"
    >
        {/* Banner */}
        <div className="relative h-36 shrink-0 bg-gray-200 sm:h-40">
            <Bone className="absolute left-4 top-4 h-6 w-28 bg-gray-300" />
            <Bone className="absolute bottom-3 left-4 h-6 w-48 bg-gray-300" />
        </div>

        {/* Body */}
        <div className="relative flex flex-1 flex-col px-4 pb-5 pt-4 sm:px-5">
            <span className="absolute -top-12 right-4 h-20 w-20 rounded-2xl border-4 border-solid border-white bg-gray-200 shadow-md sm:right-5 sm:h-[88px] sm:w-[88px]" />

            <Bone className="h-4 w-32" />
            <Bone className="mt-3 h-6 w-3/5" />
            <Bone className="mt-2 h-4 w-2/5" />

            <Bone className="mt-5 h-3.5 w-full" />
            <Bone className="mt-2 h-3.5 w-4/5" />

            <div className="min-h-[16px] flex-1" />

            <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-solid border-gray-100 [&>div:nth-child(even)]:border-l [&>div:nth-child(n+3)]:border-t [&>div]:border-0 [&>div]:border-solid [&>div]:border-gray-100">
                {TB.range(6).map(index => (
                    <div key={`skeleton-detail-${index}`} className="px-3 py-2.5">
                        <Bone className="h-2.5 w-16" />
                        <Bone className="mt-2 h-4 w-24" />
                    </div>
                ))}
            </div>

            <div className="mt-5 flex flex-col gap-2.5">
                <Bone className="h-11 w-full rounded-lg" />
                <Bone className="h-11 w-full rounded-lg bg-gray-100" />
            </div>
        </div>
    </div>
);

JobBoardCard.propTypes = {
    job: PropTypes.object.isRequired,
    disabled: PropTypes.any,
    onUpdateJob: PropTypes.func,
    onRequest: PropTypes.func,
};

export default JobBoardCard;
